/* eslint-disable no-console */
import { client } from "."; // ← el cliente oficial de Supabase

/**
 * Fase 1 — vista de curación de contactos en /admin/usuarios (read-only).
 *
 * Dos poblaciones distintas se unen en una sola lista de "pendientes":
 *   - NUEVOS: emails vistos en crm_actividades que todavía no existen en `usuarios`.
 *     Vienen del RPC `crm_contactos_pendientes` (migración 20260719), que agrega
 *     ~53k filas del lado de la DB y aplica el pre-filtro de ruido.
 *   - SIN CABLEAR: usuarios que sí existen pero sin cliente_id ni agencia.
 *     Salen del array de usuarios que la página ya tiene cargado — sin query extra.
 */

export const ORIGEN_NUEVO = "nuevo";
export const ORIGEN_SIN_CABLEAR = "sin_cablear";

export const ESTADO_SOLO_MAILING = "solo_mailing";
export const ESTADO_DESCARTADO = "descartado";

export const BLACKLIST_EMAIL = "email";
export const BLACKLIST_DOMINIO = "dominio";

/** Trae los emails vistos en el CRM que no existen como usuario. */
export const getContactosPendientes = async ({ pais = "CHI" } = {}) => {
  try {
    const { data, error } = await client.rpc("crm_contactos_pendientes", { p_pais: pais });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error("Error en getContactosPendientes:", error);
    return [];
  }
};

/**
 * ¿Ya existe un usuario con este email en este país?
 *
 * Consulta la DB EN VIVO, no el array en memoria: `usuarios` no tiene UNIQUE
 * sobre email (solo PK en id), y el array de la página puede venir recortado
 * — getUsers corta en 5000 filas y, para quien no es admin, excluye los
 * deshabilitados. Validar contra memoria deja crear duplicados de usuarios
 * anulados sin que nadie lo note.
 *
 * Devuelve { existe, usuario, error }. Ante error de red devuelve existe=null
 * para que el llamador NO interprete el fallo como "no existe" y siga insertando.
 */
export const existeUsuarioConEmail = async ({ email, pais }) => {
  const emailNormalizado = String(email || "")
    .trim()
    .toLowerCase();

  if (!emailNormalizado || !pais) {
    return { existe: null, usuario: null, error: new Error("email y pais son requeridos") };
  }

  try {
    // ilike puede traer de más: `_` y `%` son comodines en LIKE y hay emails con
    // guión bajo. Se usa como prefiltro barato y la igualdad se decide en JS.
    const { data, error } = await client
      .from("usuarios")
      .select("id, email, nombre, apellido, rol, habilitado")
      .ilike("email", emailNormalizado)
      .eq("pais", pais)
      .limit(50);

    if (error) throw error;

    const match = (data || []).find((u) => String(u.email || "").toLowerCase() === emailNormalizado);

    return { existe: Boolean(match), usuario: match || null, error: null };
  } catch (error) {
    console.error("Error en existeUsuarioConEmail:", error);
    return { existe: null, usuario: null, error };
  }
};

/**
 * Trae los contactos ya curados de un país, separados por estado.
 *
 * Consumidor: el Mailing. Los `solo_mailing` se suman a los destinatarios como
 * una categoría más; los `descartado` se restan siempre, incluso si el mismo
 * email existe además como usuario habilitado.
 *
 * Devuelve emails normalizados a minúscula y deduplicados. Ante error devuelve
 * listas vacías: el mailing sigue funcionando con los usuarios de siempre.
 */
export const getContactosCurados = async ({ pais }) => {
  try {
    const { data, error } = await client
      .from("contactos")
      .select("email, nombre, apellido, estado")
      .eq("pais", pais)
      .limit(5000);

    if (error) throw error;

    const soloMailing = [];
    const descartados = [];

    (data || []).forEach((c) => {
      const email = String(c.email || "")
        .trim()
        .toLowerCase();
      if (!email.includes("@")) return;

      if (c.estado === ESTADO_SOLO_MAILING) {
        soloMailing.push({ email, nombre: `${c.nombre || ""} ${c.apellido || ""}`.trim() });
      } else if (c.estado === ESTADO_DESCARTADO) {
        descartados.push(email);
      }
    });

    return { soloMailing, descartados: [...new Set(descartados)], error: null };
  } catch (error) {
    console.error("Error en getContactosCurados:", error);
    return { soloMailing: [], descartados: [], error };
  }
};

/**
 * Lista las filas completas de `contactos` de un estado + país, más nuevas
 * primero. Para las vistas de gestión (Descartados / Solo mailing). Ante error
 * devuelve [] para no romper la vista.
 */
export const listarContactos = async ({ estado, pais }) => {
  try {
    const { data, error } = await client
      .from("contactos")
      .select("id, email, nombre, apellido, motivo, created_at")
      .eq("estado", estado)
      .eq("pais", pais)
      .order("created_at", { ascending: false })
      .limit(5000);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en listarContactos:", error);
    return [];
  }
};

/**
 * COUNT barato de `contactos` por estado + país (head: true → solo el total).
 * Para los labels "Descartados (N)" / "Solo mailing (N)". -1 ante error.
 */
export const contarContactos = async ({ estado, pais }) => {
  try {
    const { count, error } = await client
      .from("contactos")
      .select("id", { count: "exact", head: true })
      .eq("estado", estado)
      .eq("pais", pais);
    if (error) throw error;
    return count ?? 0;
  } catch (error) {
    console.error("Error en contarContactos:", error);
    return -1;
  }
};

/**
 * Quita una fila de `contactos` por id. DELETE .select() para distinguir el
 * borrado real (data.length > 0) de un bloqueo de RLS (0 filas, sin throw).
 * Al borrar, el email vuelve a aparecer en pendientes (el RPC ya no lo excluye).
 * Devuelve { ok, error }.
 */
export const quitarContacto = async (id) => {
  const idNumerico = Number(id);
  if (!idNumerico) {
    return { ok: false, error: new Error("id es requerido") };
  }

  try {
    const { data, error } = await client.from("contactos").delete().eq("id", idNumerico).select();

    if (error) throw error;
    if (!data || data.length === 0) {
      return { ok: false, error: new Error("No se borró ninguna fila") };
    }

    return { ok: true, error: null };
  } catch (error) {
    console.error("Error en quitarContacto:", error);
    return { ok: false, error };
  }
};

/**
 * Trae el blacklist que aplica a un país para el armado del envío: entradas
 * globales (pais NULL) + las del país. Separa emails y dominios, normalizados.
 *
 * Consumidor: el Mailing (primera capa de exclusión). El guardián real es
 * email-blast, que vuelve a filtrar del lado servidor. Ante error devuelve
 * listas vacías con la bandera `error` para que la UI pueda avisar.
 */
export const getBlacklistParaEnvio = async ({ pais }) => {
  try {
    const { data, error } = await client
      .from("blacklist")
      .select("tipo, valor, pais")
      .or(`pais.is.null,pais.eq.${pais}`)
      .limit(5000);

    if (error) throw error;

    const emails = [];
    const dominios = [];
    (data || []).forEach((b) => {
      const valor = String(b.valor || "")
        .trim()
        .toLowerCase();
      if (!valor) return;
      if (b.tipo === BLACKLIST_EMAIL) emails.push(valor);
      else if (b.tipo === BLACKLIST_DOMINIO) dominios.push(valor);
    });

    return { emails: [...new Set(emails)], dominios: [...new Set(dominios)], error: null };
  } catch (error) {
    console.error("Error en getBlacklistParaEnvio:", error);
    return { emails: [], dominios: [], error };
  }
};

/**
 * Ata un usuario existente a su entidad. NO es un alta.
 *
 * Escribe UNA sola columna —`agencia` o `cliente_id` según el rol— y filtra por
 * id. No reusa `updateUsuario` a propósito: ese util escribe `email` de forma
 * incondicional (`email: email?.trim() || null`), así que llamarlo sin email
 * dejaría el correo en NULL. Acá se toca lo mínimo y nada más.
 *
 * Devuelve { ok, usuario, error }.
 */
export const vincularUsuarioEntidad = async ({ id, rol, entidadId }) => {
  const idNumerico = Number(id);
  const entidadNumerica = Number(entidadId);

  if (!idNumerico || !entidadNumerica) {
    return { ok: false, usuario: null, error: new Error("id y entidadId son requeridos") };
  }
  if (!["agencia", "cliente"].includes(rol)) {
    return { ok: false, usuario: null, error: new Error(`Rol no vinculable: ${rol}`) };
  }

  try {
    const updates = rol === "agencia" ? { agencia: entidadNumerica } : { cliente_id: entidadNumerica };

    const { data, error } = await client.from("usuarios").update(updates).eq("id", idNumerico).select();

    if (error) throw error;
    // Sin filas devueltas = RLS bloqueó el update. No es éxito.
    if (!data || data.length === 0) {
      return { ok: false, usuario: null, error: new Error("No se actualizó ninguna fila") };
    }

    return { ok: true, usuario: data[0], error: null };
  } catch (error) {
    console.error("Error en vincularUsuarioEntidad:", error);
    return { ok: false, usuario: null, error };
  }
};

/**
 * Emails ya existentes en `usuarios` de un dominio + país, como Set en minúscula.
 * Chequeo BATCH (1 query) para el alta masiva por dominio — evita N SELECTs.
 * El `ilike('%@dominio')` prefiltra y la igualdad de dominio se reconfirma en JS
 * (robusto ante subdominios). Ante error devuelve Set vacío + la bandera.
 */
export const emailsExistentesUsuarios = async ({ dominio, pais }) => {
  const dom = String(dominio || "")
    .trim()
    .toLowerCase();
  if (!dom || !pais) return { emails: new Set(), error: new Error("dominio y pais requeridos") };

  try {
    const { data, error } = await client
      .from("usuarios")
      .select("email")
      .ilike("email", `%@${dom}`)
      .eq("pais", pais)
      .limit(5000);

    if (error) throw error;

    const emails = new Set(
      (data || []).map((u) => String(u.email || "").toLowerCase()).filter((e) => (e.split("@")[1] || "") === dom)
    );
    return { emails, error: null };
  } catch (error) {
    console.error("Error en emailsExistentesUsuarios:", error);
    return { emails: new Set(), error };
  }
};

/**
 * Alta de UN usuario desde el flujo de pendientes (batch-safe). NO reusa
 * `saveUsuario` porque ese muestra un toast por error — en un alta masiva daría
 * N toasts. Inserta con `habilitado=true` explícito; entidad según rol (edificio
 * sin entidad). Maneja 23505 (UNIQUE email+pais) como "ya existía", sin throw.
 * Devuelve { ok, duplicado, error }.
 */
export const crearUsuarioDesdePendiente = async ({ email, nombre, apellido, rol, entidadId, pais }) => {
  const emailNorm = String(email || "")
    .trim()
    .toLowerCase();
  if (!emailNorm || !pais || !rol) {
    return { ok: false, duplicado: false, error: new Error("email, pais y rol requeridos") };
  }
  // Normalizar rol a minúscula: la app nunca debe escribir roles con mayúsculas (defensa en profundidad)
  const rolNorm = String(rol).trim().toLowerCase();

  try {
    const { data, error } = await client
      .from("usuarios")
      .insert({
        email: emailNorm,
        nombre: nombre || null,
        apellido: apellido || null,
        rol: rolNorm,
        pais,
        habilitado: true,
        edificiosOk: null,
        agencia: rolNorm === "agencia" ? Number(entidadId) : null,
        cliente_id: rolNorm === "cliente" ? Number(entidadId) : null,
      })
      .select();

    if (error) {
      if (error.code === "23505") return { ok: false, duplicado: true, error: null };
      throw error;
    }
    return { ok: true, duplicado: false, usuario: data?.[0] || null, error: null };
  } catch (error) {
    console.error("Error en crearUsuarioDesdePendiente:", error);
    return { ok: false, duplicado: false, error };
  }
};

/**
 * Emails ya existentes en `contactos` de un dominio + país (CUALQUIER estado),
 * como Set en minúscula. Chequeo BATCH (1 query) para el descarte masivo por
 * dominio — mismo patrón que emailsExistentesUsuarios. La UNIQUE de contactos es
 * (lower(email), pais) sin importar estado, así que "ya existe" = ya curado.
 */
export const emailsExistentesContactos = async ({ dominio, pais }) => {
  const dom = String(dominio || "")
    .trim()
    .toLowerCase();
  if (!dom || !pais) return { emails: new Set(), error: new Error("dominio y pais requeridos") };

  try {
    const { data, error } = await client
      .from("contactos")
      .select("email")
      .ilike("email", `%@${dom}`)
      .eq("pais", pais)
      .limit(5000);

    if (error) throw error;

    const emails = new Set(
      (data || []).map((c) => String(c.email || "").toLowerCase()).filter((e) => (e.split("@")[1] || "") === dom)
    );
    return { emails, error: null };
  } catch (error) {
    console.error("Error en emailsExistentesContactos:", error);
    return { emails: new Set(), error };
  }
};

/**
 * Cura un contacto: lo manda a `contactos` como solo_mailing o descartado.
 *
 * El email se normaliza a minúscula antes de escribir, así que el chequeo previo
 * con .eq() alcanza (no se usa .ilike porque `_` es comodín en LIKE y hay emails
 * con guión bajo). El pre-chequeo es cortesía para dar un mensaje claro: el
 * candado real es el UNIQUE (lower(email), pais), y por eso 23505 se maneja
 * aparte — entre el SELECT y el INSERT puede haber otra sesión.
 *
 * Devuelve { ok, duplicado, error } en vez de tirar: la vista decide el toast.
 */
export const guardarContacto = async ({ email, pais, nombre, apellido, estado, motivo, creadoPor }) => {
  const emailNormalizado = String(email || "")
    .trim()
    .toLowerCase();

  if (!emailNormalizado || !pais) {
    return { ok: false, duplicado: false, error: new Error("email y pais son requeridos") };
  }

  try {
    const { data: existente, error: errorLookup } = await client
      .from("contactos")
      .select("id, estado")
      .eq("email", emailNormalizado)
      .eq("pais", pais)
      .maybeSingle();

    if (errorLookup) throw errorLookup;
    if (existente) {
      return { ok: false, duplicado: true, estadoExistente: existente.estado, error: null };
    }

    const { data, error } = await client
      .from("contactos")
      .insert({
        email: emailNormalizado,
        pais,
        nombre: nombre || null,
        apellido: apellido || null,
        estado,
        motivo: motivo?.trim() || null,
        creado_por: creadoPor ? Number(creadoPor) : null,
      })
      .select()
      .single();

    if (error) {
      // 23505 = violación del UNIQUE. No es un fallo: alguien más ya lo curó.
      if (error.code === "23505") {
        return { ok: false, duplicado: true, error: null };
      }
      throw error;
    }

    return { ok: true, duplicado: false, contacto: data, error: null };
  } catch (error) {
    console.error("Error en guardarContacto:", error);
    return { ok: false, duplicado: false, error };
  }
};

/**
 * Agrega un valor a la lista de bloqueo (`blacklist`).
 *
 * `tipo='email'` bloquea un correo puntual; `tipo='dominio'` bloquea todo un
 * dominio (el valor va sin el @). `pais=null` = ambos países.
 *
 * El valor se normaliza (trim + minúscula); para dominio se saca cualquier `@…`
 * pegado por si el usuario pega un email completo. Igual que guardarContacto: el
 * candado real es el UNIQUE (tipo, lower(valor), coalesce(pais,'GLOBAL')), así
 * que 23505 se maneja aparte y no se trata como fallo.
 *
 * Devuelve { ok, duplicado, error }; la vista decide el toast.
 */
export const agregarBlacklist = async ({ tipo, valor, pais = null, motivo, creadoPor }) => {
  if (![BLACKLIST_EMAIL, BLACKLIST_DOMINIO].includes(tipo)) {
    return { ok: false, duplicado: false, error: new Error(`Tipo de blacklist inválido: ${tipo}`) };
  }

  let valorNormalizado = String(valor || "")
    .trim()
    .toLowerCase();
  if (tipo === BLACKLIST_DOMINIO && valorNormalizado.includes("@")) {
    valorNormalizado = valorNormalizado.split("@").pop();
  }

  if (!valorNormalizado) {
    return { ok: false, duplicado: false, error: new Error("valor es requerido") };
  }

  try {
    const { data, error } = await client
      .from("blacklist")
      .insert({
        tipo,
        valor: valorNormalizado,
        pais: pais || null,
        motivo: motivo?.trim() || null,
        creado_por: creadoPor ? Number(creadoPor) : null,
      })
      .select()
      .single();

    if (error) {
      // 23505 = ya está en la lista. No es un fallo.
      if (error.code === "23505") {
        return { ok: false, duplicado: true, error: null };
      }
      throw error;
    }

    return { ok: true, duplicado: false, entrada: data, error: null };
  } catch (error) {
    console.error("Error en agregarBlacklist:", error);
    return { ok: false, duplicado: false, error };
  }
};

/**
 * Lista todas las entradas del blacklist (ambos países), más nuevas primero.
 * Para el panel de gestión. Ante error devuelve [] para no romper el panel.
 */
export const listarBlacklist = async () => {
  try {
    const { data, error } = await client
      .from("blacklist")
      .select("id, tipo, valor, pais, motivo, created_at")
      .order("created_at", { ascending: false })
      .limit(5000);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en listarBlacklist:", error);
    return [];
  }
};

/**
 * COUNT barato de la blacklist (head: true → no trae filas, solo el total).
 * Para el label "Blacklist (N)" del <select> sin cargar la vista. -1 ante error.
 */
export const contarBlacklist = async () => {
  try {
    const { count, error } = await client.from("blacklist").select("id", { count: "exact", head: true });
    if (error) throw error;
    return count ?? 0;
  } catch (error) {
    console.error("Error en contarBlacklist:", error);
    return -1;
  }
};

/**
 * Quita una entrada del blacklist por id. DELETE .select() para distinguir el
 * borrado real (data.length > 0) de un bloqueo de RLS (0 filas, sin throw).
 * Devuelve { ok, error }.
 */
export const quitarBlacklist = async (id) => {
  const idNumerico = Number(id);
  if (!idNumerico) {
    return { ok: false, error: new Error("id es requerido") };
  }

  try {
    const { data, error } = await client.from("blacklist").delete().eq("id", idNumerico).select();

    if (error) throw error;
    if (!data || data.length === 0) {
      return { ok: false, error: new Error("No se borró ninguna fila") };
    }

    return { ok: true, error: null };
  } catch (error) {
    console.error("Error en quitarBlacklist:", error);
    return { ok: false, error };
  }
};

/**
 * Infiere nombre y apellido del local-part del email.
 * "maria.osses@dentsu.com" → { nombre: "Maria", apellido: "Osses" }
 * Es una heurística para pre-llenar el alta, no un dato confiable: los emails
 * de casilla genérica ("adops", "ventas") devuelven apellido vacío.
 */
export const inferirNombreApellido = (email) => {
  const localPart = String(email || "")
    .split("@")[0]
    .toLowerCase();

  const partes = localPart
    .split(/[._]+/)
    .map((p) => p.replace(/\d+/g, "").trim())
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1));

  return {
    nombre: partes[0] || "",
    apellido: partes.slice(1).join(" ") || "",
  };
};

/**
 * Mapa dominio → entidad, construido con los usuarios YA cableados del país.
 * Solo se conservan los dominios que apuntan a una única entidad: los holdings
 * de agencias (omc.com, dentsu.com…) cubren varias y no desambiguan nada, así
 * que se descartan en vez de sugerir algo incorrecto.
 */
export const construirMapaDominios = (usuarios = [], paisCode) => {
  const porDominio = new Map();

  usuarios.forEach((u) => {
    if (u.pais !== paisCode || !u.email?.includes("@")) return;
    if (u.agencia == null && u.cliente_id == null) return;

    const dominio = u.email.toLowerCase().split("@")[1];
    if (!dominio) return;

    const esAgencia = u.agencia != null;
    const clave = esAgencia ? `A${u.agencia}` : `C${u.cliente_id}`;
    const actual = porDominio.get(dominio);

    if (!actual) {
      porDominio.set(dominio, {
        clave,
        tipo: esAgencia ? "agencia" : "cliente",
        entidadId: Number(esAgencia ? u.agencia : u.cliente_id),
        ambiguo: false,
      });
    } else if (actual.clave !== clave) {
      actual.ambiguo = true;
    }
  });

  const mapa = new Map();
  porDominio.forEach((valor, dominio) => {
    if (!valor.ambiguo) mapa.set(dominio, valor);
  });

  return mapa;
};

/**
 * Une NUEVOS + SIN CABLEAR en las filas que consume la tabla.
 * Orden: ultima_fecha desc, luego veces_visto desc. Los SIN CABLEAR no tienen
 * actividad asociada en esta fase (ultima_fecha null) → caen al final.
 */
export const construirFilasPendientes = ({ pendientes = [], usuarios = [], paisCode, mapaDominios }) => {
  const mapa = mapaDominios || construirMapaDominios(usuarios, paisCode);

  const resolverSugerencia = (email) => {
    const dominio = String(email || "")
      .toLowerCase()
      .split("@")[1];
    return dominio ? mapa.get(dominio) || null : null;
  };

  const filasNuevos = pendientes.map((p) => ({
    id: `nuevo-${p.email}`,
    origen: ORIGEN_NUEVO,
    email: p.email,
    ...inferirNombreApellido(p.email),
    dominio: p.email.split("@")[1] || "",
    vecesVisto: Number(p.veces_visto) || 0,
    primeraFecha: p.primera_fecha || null,
    ultimaFecha: p.ultima_fecha || null,
    sugerencia: resolverSugerencia(p.email),
  }));

  const filasSinCablear = usuarios
    .filter(
      (u) =>
        u.pais === paisCode &&
        u.cliente_id == null &&
        u.agencia == null &&
        ["agencia", "cliente"].includes(u.rol) &&
        u.email?.includes("@")
    )
    .map((u) => {
      const inferido = inferirNombreApellido(u.email);
      return {
        id: `usuario-${u.id}`,
        usuarioId: u.id,
        origen: ORIGEN_SIN_CABLEAR,
        // El rol ya está definido en la fila de usuarios: decide qué selector de
        // entidad se muestra al vincular (intermediarios vs clientes).
        rol: u.rol,
        email: u.email.toLowerCase(),
        // El usuario ya existe: si tiene nombre cargado, gana sobre la inferencia.
        nombre: u.nombre || inferido.nombre,
        apellido: u.apellido || inferido.apellido,
        dominio: u.email.toLowerCase().split("@")[1] || "",
        vecesVisto: 0,
        primeraFecha: null,
        ultimaFecha: null,
        sugerencia: resolverSugerencia(u.email),
      };
    });

  return [...filasNuevos, ...filasSinCablear].sort((a, b) => {
    if (a.ultimaFecha !== b.ultimaFecha) {
      if (!a.ultimaFecha) return 1;
      if (!b.ultimaFecha) return -1;
      return b.ultimaFecha.localeCompare(a.ultimaFecha);
    }
    return b.vecesVisto - a.vecesVisto;
  });
};

// ─── Rebotes (FASE R2) ───────────────────────────────────────────────────────
// Curación de bounces detectados por el edge mailing-rebotes-scan → crm_rebotes.
// Un rebote "duro" marcado como obsoleto sale del circuito (mailing + pendientes).

export const REBOTE_DURO = "duro";
export const REBOTE_BLANDO = "blando";
export const REBOTE_ESTADO_PENDIENTE = "pendiente";
export const REBOTE_ESTADO_PROCESADO = "procesado";
export const REBOTE_ACCION_OBSOLETO = "obsoleto";

/**
 * ¿Cuáles de estos emails ya existen en `usuarios` de un país? Set en minúscula.
 * Chequeo BATCH (1 query) por LISTA de emails — hermano de emailsExistentesUsuarios
 * (que va por dominio). Uso: anotar "dónde está" en la vista de rebotes (display).
 * Los emails se normalizan a minúscula; `usuarios` los guarda igual, así que el
 * `.in()` exacto alcanza. Ante error devuelve Set vacío + la bandera.
 */
export const emailsExistentesUsuariosPorLista = async ({ emails, pais }) => {
  const lista = [
    ...new Set(
      (emails || [])
        .map((e) =>
          String(e || "")
            .trim()
            .toLowerCase()
        )
        .filter((e) => e.includes("@"))
    ),
  ];
  if (lista.length === 0 || !pais) return { emails: new Set(), error: null };

  try {
    const { data, error } = await client
      .from("usuarios")
      .select("email")
      .in("email", lista)
      .eq("pais", pais)
      .limit(5000);

    if (error) throw error;

    const set = new Set((data || []).map((u) => String(u.email || "").toLowerCase()));
    return { emails: set, error: null };
  } catch (error) {
    console.error("Error en emailsExistentesUsuariosPorLista:", error);
    return { emails: new Set(), error };
  }
};

/** Igual que el anterior pero contra `contactos` (cualquier estado) de un país. */
export const emailsExistentesContactosPorLista = async ({ emails, pais }) => {
  const lista = [
    ...new Set(
      (emails || [])
        .map((e) =>
          String(e || "")
            .trim()
            .toLowerCase()
        )
        .filter((e) => e.includes("@"))
    ),
  ];
  if (lista.length === 0 || !pais) return { emails: new Set(), error: null };

  try {
    const { data, error } = await client
      .from("contactos")
      .select("email")
      .in("email", lista)
      .eq("pais", pais)
      .limit(5000);

    if (error) throw error;

    const set = new Set((data || []).map((c) => String(c.email || "").toLowerCase()));
    return { emails: set, error: null };
  } catch (error) {
    console.error("Error en emailsExistentesContactosPorLista:", error);
    return { emails: new Set(), error };
  }
};

/**
 * Filas de `crm_rebotes` (por defecto duros pendientes) de un país, más nuevas
 * primero. A cada fila le anota `dondeEsta` ('usuario' | 'contacto' | null) —
 * SOLO para mostrar en qué padrón está el correo, sin lógica de negocio.
 * Ante error devuelve [] para no romper la vista.
 */
export const getRebotes = async ({ pais, estado = REBOTE_ESTADO_PENDIENTE, tipo = REBOTE_DURO }) => {
  try {
    const { data, error } = await client
      .from("crm_rebotes")
      .select("id, email, tipo, smtp_status, diagnostic, fecha_rebote, estado, procesado_accion")
      .eq("pais", pais)
      .eq("estado", estado)
      .eq("tipo", tipo)
      .order("fecha_rebote", { ascending: false })
      .limit(5000);

    if (error) throw error;

    const filas = data || [];
    const emails = filas.map((r) => r.email);
    const [{ emails: enUsuarios }, { emails: enContactos }] = await Promise.all([
      emailsExistentesUsuariosPorLista({ emails, pais }),
      emailsExistentesContactosPorLista({ emails, pais }),
    ]);

    const anotadas = filas.map((r) => ({
      ...r,
      // Usuario tiene prioridad sobre contacto (solo display).
      dondeEsta: enUsuarios.has(r.email) ? "usuario" : enContactos.has(r.email) ? "contacto" : null,
    }));

    return { filas: anotadas, error: null };
  } catch (error) {
    console.error("Error en getRebotes:", error);
    return { filas: [], error };
  }
};

/**
 * COUNT barato de crm_rebotes por estado + tipo + país (head: true → solo total).
 * Para el label "Rebotes (N)" del <select>. -1 ante error.
 */
export const contarRebotes = async ({ pais, estado = REBOTE_ESTADO_PENDIENTE, tipo = REBOTE_DURO }) => {
  try {
    const { count, error } = await client
      .from("crm_rebotes")
      .select("id", { count: "exact", head: true })
      .eq("pais", pais)
      .eq("estado", estado)
      .eq("tipo", tipo);
    if (error) throw error;
    return count ?? 0;
  } catch (error) {
    console.error("Error en contarRebotes:", error);
    return -1;
  }
};

/**
 * Marca un rebote como obsoleto: estado='procesado', procesado_accion='obsoleto'.
 * NO borra la fila, NO anula, NO toca `usuarios` ni `contactos`. `creado_por` se
 * sobrescribe con el usuario que ejecuta la acción (quién lo procesó).
 * UPDATE .select() para distinguir el éxito real de un bloqueo de RLS (0 filas).
 * Devuelve { ok, error }.
 */
export const marcarReboteObsoleto = async ({ id, creadoPor }) => {
  const idNumerico = Number(id);
  if (!idNumerico) {
    return { ok: false, error: new Error("id es requerido") };
  }

  try {
    const { data, error } = await client
      .from("crm_rebotes")
      .update({
        estado: REBOTE_ESTADO_PROCESADO,
        procesado_accion: REBOTE_ACCION_OBSOLETO,
        creado_por: creadoPor ? Number(creadoPor) : null,
      })
      .eq("id", idNumerico)
      .select();

    if (error) throw error;
    if (!data || data.length === 0) {
      return { ok: false, error: new Error("No se actualizó ninguna fila") };
    }

    return { ok: true, error: null };
  } catch (error) {
    console.error("Error en marcarReboteObsoleto:", error);
    return { ok: false, error };
  }
};

/**
 * Marca EN BLOQUE todos los rebotes duros pendientes de un país como obsoletos
 * (UPDATE batch, 1 query). Mismo efecto que marcarReboteObsoleto por fila, pero
 * sobre el universo entero. `.select("id")` devuelve las filas afectadas para
 * saber cuántas se marcaron y distinguir un bloqueo de RLS (0 filas) del éxito.
 * Devuelve { ok, count, error }.
 */
export const marcarRebotesObsoletosBulk = async ({ pais, creadoPor }) => {
  if (!pais) return { ok: false, count: 0, error: new Error("pais es requerido") };

  try {
    const { data, error } = await client
      .from("crm_rebotes")
      .update({
        estado: REBOTE_ESTADO_PROCESADO,
        procesado_accion: REBOTE_ACCION_OBSOLETO,
        creado_por: creadoPor ? Number(creadoPor) : null,
      })
      .eq("pais", pais)
      .eq("estado", REBOTE_ESTADO_PENDIENTE)
      .eq("tipo", REBOTE_DURO)
      .select("id");

    if (error) throw error;
    return { ok: true, count: (data || []).length, error: null };
  } catch (error) {
    console.error("Error en marcarRebotesObsoletosBulk:", error);
    return { ok: false, count: 0, error };
  }
};

/**
 * Emails marcados como obsoletos (estado='procesado', procesado_accion='obsoleto')
 * de un país, normalizados y deduplicados. Consumidor: el Mailing — se restan
 * siempre al armar la lista, igual que descartados + blacklist. Ante error
 * devuelve lista vacía con la bandera `error` para no romper el envío.
 */
export const getRebotesObsoletos = async ({ pais }) => {
  try {
    const { data, error } = await client
      .from("crm_rebotes")
      .select("email")
      .eq("pais", pais)
      .eq("estado", REBOTE_ESTADO_PROCESADO)
      .eq("procesado_accion", REBOTE_ACCION_OBSOLETO)
      .limit(5000);

    if (error) throw error;

    const emails = [
      ...new Set(
        (data || [])
          .map((r) =>
            String(r.email || "")
              .trim()
              .toLowerCase()
          )
          .filter((e) => e.includes("@"))
      ),
    ];
    return { emails, error: null };
  } catch (error) {
    console.error("Error en getRebotesObsoletos:", error);
    return { emails: [], error };
  }
};
